/**
 * Date picker dialog for selecting month and year
 * Two-column layout: months on left, years on right
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { CalendarDays } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

// Czech month names
const MONTHS = [
	{ value: 0, label: 'Leden' },
	{ value: 1, label: 'Únor' },
	{ value: 2, label: 'Březen' },
	{ value: 3, label: 'Duben' },
	{ value: 4, label: 'Květen' },
	{ value: 5, label: 'Červen' },
	{ value: 6, label: 'Červenec' },
	{ value: 7, label: 'Srpen' },
	{ value: 8, label: 'Září' },
	{ value: 9, label: 'Říjen' },
	{ value: 10, label: 'Listopad' },
	{ value: 11, label: 'Prosinec' },
] as const

// Years range: 2016 to 2035
const START_YEAR = 2018
const END_YEAR = 2035
const YEARS = Array.from({ length: END_YEAR - START_YEAR + 1 }, (_, i) => START_YEAR + i)

// Height of each year button in pixels (for scroll calculation)
const YEAR_ITEM_HEIGHT = 40

interface DatePickerDialogProps {
	open: boolean
	onClose: () => void
	currentDate: Date
	onSelect: (date: Date) => void
}

export function DatePickerDialog({
	open,
	onClose,
	currentDate,
	onSelect,
}: DatePickerDialogProps) {
	const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth())
	const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear())
	const yearsContainerRef = useRef<HTMLDivElement>(null)

	// Reset selection and scroll to current year when dialog opens
	const handleOpenChange = useCallback((isOpen: boolean) => {
		if (isOpen) {
			setSelectedMonth(currentDate.getMonth())
			setSelectedYear(currentDate.getFullYear())
		} else {
			onClose()
		}
	}, [currentDate, onClose])

	// Scroll years container to center the current year
	useEffect(() => {
		if (open && yearsContainerRef.current) {
			const yearIndex = currentDate.getFullYear() - START_YEAR
			const containerHeight = yearsContainerRef.current.clientHeight
			// Calculate scroll position to center the current year
			const scrollTop = (yearIndex * YEAR_ITEM_HEIGHT) - (containerHeight / 2) + (YEAR_ITEM_HEIGHT / 2)
			yearsContainerRef.current.scrollTop = Math.max(0, scrollTop)
		}
	}, [open, currentDate])

	const handleConfirm = useCallback(() => {
		// Create date for the first day of selected month/year
		const newDate = new Date(selectedYear, selectedMonth, 1)
		onSelect(newDate)
		onClose()
	}, [selectedMonth, selectedYear, onSelect, onClose])

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="max-w-[720px] max-h-[90vh] flex flex-col overflow-hidden">
				<DialogHeader className="flex-shrink-0">
					<DialogTitle className="flex items-center gap-3">
						<span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#f5ebe0]">
							<CalendarDays className="w-5 h-5 text-[#d7ad6c]" />
						</span>
						<span>Vyberte měsíc a rok</span>
					</DialogTitle>
				</DialogHeader>

				<div className="grid grid-cols-2 gap-4 mt-4 min-h-0 flex-1">
					{/* Months column */}
					<div className="flex flex-col min-h-0">
						<h3 className="text-sm font-medium text-[#8c8275] mb-2 px-2 text-center flex-shrink-0">Měsíc</h3>
						<div className="border-b border-[#e8e4df] mb-2 flex-shrink-0" />
						<div
							className="overflow-y-auto pr-1 min-h-0"
							style={{ scrollbarWidth: 'thin', scrollbarColor: '#d7ad6c #f5ebe0' }}
						>
							{MONTHS.map((month) => (
								<button
									key={month.value}
									type="button"
									onClick={() => setSelectedMonth(month.value)}
									className={`w-full text-center px-3 py-2 rounded-lg text-sm transition-colors ${
										selectedMonth === month.value
											? 'bg-[#d7ad6c] text-white font-medium'
											: 'text-[#3d3428] hover:bg-[#f5ebe0]'
									}`}
								>
									{month.label}
								</button>
							))}
						</div>
					</div>

					{/* Years column */}
					<div className="flex flex-col min-h-0">
						<h3 className="text-sm font-medium text-[#8c8275] mb-2 px-2 text-center flex-shrink-0">Rok</h3>
						<div className="border-b border-[#e8e4df] mb-2 flex-shrink-0" />
						<div
							ref={yearsContainerRef}
							className="overflow-y-auto pr-1 min-h-0"
							style={{ scrollbarWidth: 'thin', scrollbarColor: '#d7ad6c #f5ebe0' }}
						>
							{YEARS.map((year) => (
								<button
									key={year}
									type="button"
									onClick={() => setSelectedYear(year)}
									className={`w-full text-center px-3 py-2 rounded-lg text-sm transition-colors ${
										selectedYear === year
											? 'bg-[#d7ad6c] text-white font-medium'
											: 'text-[#3d3428] hover:bg-[#f5ebe0]'
									}`}
								>
									{year}
								</button>
							))}
						</div>
					</div>
				</div>

				{/* Actions */}
				<div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[#e8e4df] flex-shrink-0">
					<Button
						type="button"
						variant="outline"
						onClick={onClose}
						className="h-10 px-5 rounded-xl bg-white border border-[#e8e4df] text-[#5c5142] hover:bg-[#f5ebe0] hover:border-[#d7ad6c] transition-all"
					>
						Zrušit
					</Button>
					<Button
						type="button"
						onClick={handleConfirm}
						className="h-10 px-5 rounded-xl bg-[#d7ad6c] text-white hover:bg-[#c49b5a] transition-all shadow-sm"
					>
						Potvrdit
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
